import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Check } from "lucide-react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AvatarModal({ onClose }) {
  const { user, refresh } = useAuth();
  const overlayRef = useRef(null);
  const existing = user?.avatar || null;
  const isChange = !!(existing && (existing.emoji || existing.tag));

  // emoji-mart auto-focuses its search field on mount, which scrolls this scroll
  // container and pushes the top (search bar) off-screen. Reset to the top after mount.
  useEffect(() => {
    const t = setTimeout(() => {
      try { document.activeElement && document.activeElement.blur(); } catch { /* noop */ }
      if (overlayRef.current) overlayRef.current.scrollTop = 0;
    }, 120);
    return () => clearTimeout(t);
  }, []);
  const needsPassword = isChange && !user?.unlimited;

  const [emoji, setEmoji] = useState(existing?.emoji || "");
  const [tag, setTag] = useState(existing?.tag || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!emoji && !tag) { toast.error("Pick an emoji and up to 4 characters."); return; }
    if (needsPassword && !password) { toast.error("Enter your account password to change your avatar."); return; }
    setSaving(true);
    try {
      await api.post("/me/avatar", { emoji, tag, password: password || null });
      await refresh();
      toast.success("Your avatar is set! It now shows on all your community videos.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your avatar — try again.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="avatar-modal-overlay"
    >
      <div
        className="sms-card w-full max-w-[420px] p-5 my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="avatar-modal"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-extrabold text-[#171426]">Your Avatar</h2>
          <button data-testid="avatar-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#eef3ea] text-[#6b6880]">
            <X size={18} />
          </button>
        </div>
        <p className="text-[13px] text-[#6b6880] leading-relaxed">
          Pick an emoji and up to 4 characters — letters, numbers or symbols, like the arcade high-score days (e.g. 🌞45k$). This moniker shows on every video you post to the community.
        </p>

        <div className="flex items-center justify-center gap-2 my-4">
          <div
            data-testid="avatar-preview"
            className="inline-flex items-center gap-1 bg-black/80 text-white font-extrabold rounded-full px-3.5 py-2 text-lg shadow-md"
          >
            <span>{emoji || "🙂"}</span>
            <span className="tabular-nums tracking-wide">{tag || "___"}</span>
          </div>
        </div>

        <label className="text-[11px] font-bold text-[#6b6880] uppercase tracking-wide">Your 4 characters</label>
        <input
          data-testid="avatar-tag-input"
          value={tag}
          onChange={(e) => setTag(e.target.value.slice(0, 4))}
          maxLength={4}
          placeholder="e.g. 45k$, Art7, ★★★★"
          className="sms-input w-full px-3 py-2 text-sm mt-1 font-bold tracking-widest"
          style={{ border: "2px solid #171426" }}
        />

        {needsPassword && (
          <div className="mt-3">
            <label className="text-[11px] font-bold text-[#6b6880] uppercase tracking-wide">Account password (to change)</label>
            <input
              data-testid="avatar-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Confirm it's you"
              className="sms-input w-full px-3 py-2 text-sm mt-1"
              style={{ border: "2px solid #171426" }}
            />
          </div>
        )}

        <div className="mt-3">
          <label className="text-[11px] font-bold text-[#6b6880] uppercase tracking-wide">Search & pick your emoji</label>
          <div className="mt-1.5 flex justify-center" data-testid="avatar-emoji-picker">
            <Picker
              data={data}
              onEmojiSelect={(e) => setEmoji(e.native)}
              theme="light"
              previewPosition="none"
              skinTonePosition="none"
              navPosition="top"
              autoFocus={false}
              perLine={8}
            />
          </div>
        </div>

        <button
          data-testid="avatar-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full mt-4 flex items-center justify-center gap-2 font-extrabold text-white rounded-xl py-3 transition-colors disabled:opacity-60"
          style={{ background: "linear-gradient(160deg,#7ba46f,#4f7746)" }}
        >
          {saving ? <Loader2 size={17} className="spin" /> : <Check size={17} />}
          {saving ? "Saving…" : isChange ? "Update my avatar" : "Set my avatar"}
        </button>
        {isChange && (
          <p className="text-[11px] text-[#9a96ac] text-center mt-2">Changing your avatar instantly updates it on all your posted videos.</p>
        )}
      </div>
    </div>,
    document.body
  );
}
